import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Link,
  Grid,
  TextField,
  IconButton,
} from '@mui/material';
import React, { useState } from 'react';
import theme from 'src/darkTheme';
import { CardsAssistant } from './components/CardsAssistant';

const defaultAssistantGoals = [
  {
    id: 1,
    title: 'Warm outreach to user',
    description: `Send a warm, personalized message to see if they're still in the market.`,
    isSelected: true,
    isCustom: false,
  },
  {
    id: 2,
    title: 'Qualify leads',
    description: `Identify the most promising leads, and skip the ones that aren't a fit.`,
    isSelected: true,
    isCustom: false,
  },
  {
    id: 3,
    title: 'Keep contacts updated',
    description: `Update lead details automatically when they share new information.`,
    isSelected: true,
    isCustom: false,
  },
];

export const ShapeYourFirstAssistant = () => {
  const [assistantGoals, setAssistantGoals] = useState(defaultAssistantGoals);
  const [nextId, setNextId] = useState(4);

  const handleGoalToggle = (goalId: number) => {
    setAssistantGoals((prev) =>
      prev.map((goal) =>
        goal.id === goalId ? { ...goal, isSelected: !goal.isSelected } : goal
      )
    );
  };

  const handleAddMore = () => {
    const newGoal = {
      id: nextId,
      title: '',
      description: '',
      isSelected: true,
      isCustom: true,
    };
    setAssistantGoals((prev) => [...prev, newGoal]);
    setNextId((prev) => prev + 1);
  };

  const handleCustomGoalChange = (
    goalId: number,
    field: 'title' | 'description',
    value: string
  ) => {
    setAssistantGoals((prev) =>
      prev.map((goal) =>
        goal.id === goalId ? { ...goal, [field]: value } : goal
      )
    );
  };

  const handleDeleteGoal = (goalId: number) => {
    setAssistantGoals((prev) => prev.filter((goal) => goal.id !== goalId));
  };

  const selectedGoals = assistantGoals.filter((goal) => goal.isSelected);

  return (
    <Grid container sx={{ minHeight: '100vh' }}>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: 32, left: 32, zIndex: 2 }}>
          <img
            src="/icon/artwork.svg"
            alt="Oppy Logo"
            style={{ width: 80, height: 'auto' }}
          />
        </Box>
        <Box
          sx={{ width: '100%', maxWidth: 420, mx: 'auto', textAlign: 'center' }}
        >
          <Typography color="#A1A1AA" fontSize={16} mb={1} fontWeight={400}>
            1 of 4
          </Typography>
          <Typography
            color="#fff"
            fontFamily="Space Grotesk"
            fontWeight={700}
            fontSize={28}
            mb={1}
          >
            Shape your first assistant
          </Typography>
          <Typography color="#D1D1D6" fontSize={16} mb={4}>
            Tell us the ways your assistant can make your life easier. You'll be
            able to change this later.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {assistantGoals.map((goal) => (
              <Box
                key={goal.id}
                onClick={() => handleGoalToggle(goal.id)}
                sx={{
                  background: goal.isSelected
                    ? 'rgba(73, 73, 200, 0.1)'
                    : 'transparent',
                  border: goal.isSelected
                    ? '1.5px solid #4949C8'
                    : '1.5px solid #797979',
                  borderRadius: 4,
                  px: 2.5,
                  py: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  '&:hover': {
                    border: '1.5px solid #4949C8',
                    background: 'rgba(73, 73, 200, 0.05)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {goal.isCustom && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGoal(goal.id);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: '#A1A1AA',
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': {
                        color: '#ff4444',
                        background: 'rgba(255, 68, 68, 0.1)',
                      },
                      '.MuiBox-root:hover &': {
                        opacity: 1,
                      },
                    }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
                <Checkbox
                  checked={goal.isSelected}
                  sx={{
                    color: '#4949C8',
                    '&.Mui-checked': { color: '#4949C8' },
                    mt: 0.5,
                  }}
                  inputProps={{ 'aria-label': 'selected' }}
                />
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  {goal.isCustom ? (
                    <>
                      <TextField
                        fullWidth
                        variant="standard"
                        placeholder="Enter goal title"
                        value={goal.title}
                        onChange={(e) =>
                          handleCustomGoalChange(
                            goal.id,
                            'title',
                            e.target.value
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          mb: 1,
                          '& .MuiInput-root': {
                            color: '#fff',
                            fontSize: 16,
                            fontWeight: 500,
                            '&:before': { borderBottom: 'none' },
                            '&:after': { borderBottom: 'none' },
                            '&:hover:before': { borderBottom: 'transparent' },
                          },
                          '& .MuiInput-input::placeholder': {
                            color: '#A1A1AA',
                            opacity: 1,
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        variant="standard"
                        placeholder="Enter goal description"
                        value={goal.description}
                        onChange={(e) =>
                          handleCustomGoalChange(
                            goal.id,
                            'description',
                            e.target.value
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          '& .MuiInput-root': {
                            color: '#D1D1D6',
                            fontSize: 15,
                            '&:before': { borderBottom: 'none' },
                            '&:after': { borderBottom: 'none' },
                            '&:hover:before': { borderBottom: 'transparent' },
                          },
                          '& .MuiInput-input::placeholder': {
                            color: '#A1A1AA',
                            opacity: 1,
                          },
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography color="#fff" fontWeight={500} fontSize={16}>
                        {goal.title}
                      </Typography>
                      <Typography color="#D1D1D6" fontSize={15} mt={0.5}>
                        {goal.description}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Link
              component="button"
              onClick={handleAddMore}
              underline="hover"
              sx={{
                color: '#7B7BFF',
                fontWeight: 500,
                fontSize: 16,
                mb: 4,
                display: 'inline-block',
                cursor: 'pointer',
                '&:hover': {
                  color: '#4949C8',
                },
              }}
            >
              Add more +
            </Link>
          </Box>
          <Button
            variant="contained"
            sx={{
              width: '100%',
              mt: 3,
              background: '#4949C8',
              color: '#fff',
              fontWeight: 400,
              fontSize: 18,
              borderRadius: 2,
              textTransform: 'none',
              py: 1.5,
              boxShadow: 'none',
              '&:hover': { background: '#3535a8' },
            }}
          >
            Continue
          </Button>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          backgroundColor: '#fafafd',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CardsAssistant
          name="Matt"
          email="matt@open.oppy.pro"
          goals={selectedGoals}
        />
      </Grid>
    </Grid>
  );
};
